import { cva, type VariantProps } from 'class-variance-authority'
import { createContext, type HTMLAttributes, useContext } from 'react'

import { cn } from '@/lib/utils'

const statusVariants = cva(
  'inline-flex items-center gap-2 rounded-full px-2.5 py-0.5 text-xs font-medium',
  {
    variants: {
      status: {
        online:
          'bg-green-50 text-green-700 dark:bg-green-950 dark:text-green-400',
        offline: 'bg-gray-50 text-gray-700 dark:bg-gray-950 dark:text-gray-400',
        maintenance:
          'bg-blue-50 text-blue-700 dark:bg-blue-950 dark:text-blue-400',
        degraded:
          'bg-yellow-50 text-yellow-700 dark:bg-yellow-950 dark:text-yellow-400',
        error: 'bg-red-50 text-red-700 dark:bg-red-950 dark:text-red-400',
      },
    },
    defaultVariants: {
      status: 'offline',
    },
  }
)

const dotVariants = cva('h-2 w-2 rounded-full', {
  variants: {
    status: {
      online: 'bg-green-500',
      offline: 'bg-gray-500',
      maintenance: 'bg-blue-500',
      degraded: 'bg-yellow-500',
      error: 'bg-red-500',
    },
  },
  defaultVariants: {
    status: 'offline',
  },
})

const statusLabels = {
  online: 'Online',
  offline: 'Offline',
  maintenance: 'Maintenance',
  degraded: 'Degraded',
  error: 'Error',
}

type StatusType = 'online' | 'offline' | 'maintenance' | 'degraded' | 'error'

const StatusContext = createContext<{ status: StatusType }>({
  status: 'offline',
})

export interface StatusProps
  extends HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof statusVariants> {
  status: StatusType
  ping?: boolean
}

function Status({
  className,
  status = 'offline',
  children,
  ...props
}: StatusProps) {
  return (
    <StatusContext.Provider value={{ status }}>
      <div className={cn(statusVariants({ status }), className)} {...props}>
        {children}
      </div>
    </StatusContext.Provider>
  )
}

function StatusIndicator({
  className,
  ping = false,
  ...props
}: HTMLAttributes<HTMLDivElement> & { ping?: boolean }) {
  const { status } = useContext(StatusContext)

  return (
    <div
      className="relative inline-flex items-center justify-center"
      {...props}
    >
      <div className={cn(dotVariants({ status }), className)} />
      {ping && (
        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-current opacity-75" />
      )}
    </div>
  )
}

function StatusLabel({
  className,
  children,
  ...props
}: HTMLAttributes<HTMLSpanElement>) {
  const { status } = useContext(StatusContext)

  return (
    <span className={cn(className)} {...props}>
      {children || statusLabels[status]}
    </span>
  )
}

export { Status, StatusIndicator, StatusLabel, statusVariants }
